/**
 * Pure helpers for GA balloon labels, colours, and saved positions.
 * ESM so CRA's production webpack can import it (CJS default-import white-screens the portal).
 */

export function offsetModeKey(labelMode) {
  return labelMode === 'auto' ? 'auto' : 'margins';
}

/** Sequence slides render as 'auto' (no 300px gutters) even when the toolbar says "outside". */
export function effectiveLabelMode(labelMode, { isSequence } = {}) {
  if (isSequence && labelMode !== 'off') return 'auto';
  return labelMode || 'margins';
}

export function isPlaceholderMarkerLabel(label) {
  const s = String(label || '').trim();
  if (!s) return true;
  return /^area\s+\d+$/i.test(s);
}

function wbsLeaf(path) {
  if (!path) return null;
  const leaf = String(path).split(' > ').map((p) => p.trim()).filter(Boolean).pop();
  return leaf || null;
}

/**
 * Default balloon title from a binding + the activity list used to pick it.
 * WBS → last path segment. Activities → first activity's name (then code).
 */
export function suggestedMarkerLabel({ binding, activities = [] } = {}) {
  if (!binding) return null;
  if (binding.mode === 'wbs') {
    return wbsLeaf(binding.wbsPath);
  }
  const codes = binding.taskCodes || [];
  if (!codes.length) return null;
  const first = activities.find((a) => a.task_code === codes[0] || a.taskCode === codes[0]);
  const name = (first?.task_name || first?.name || codes[0] || '').trim();
  return name || null;
}

/** On-screen title: keep a custom name; replace empty / "Area 3" with the bound activity or WBS. */
export function displayMarkerLabel(marker, resolved) {
  const raw = String(marker?.label || '').trim();
  if (raw && !isPlaceholderMarkerLabel(raw)) return raw;
  if (marker?.binding?.mode === 'wbs') {
    const leaf = wbsLeaf(marker.binding.wbsPath);
    if (leaf) return leaf;
  }
  const acts = resolved?.activities || [];
  if (acts.length) {
    const name = (acts[0].name || acts[0].taskCode || '').trim();
    if (name) return name;
  }
  return raw || '(unnamed)';
}

export function buildLabelStyleAfterMove(style, offset, modeKey) {
  return {
    ...(style || {}),
    labelOffsets: { ...((style || {}).labelOffsets || {}), [modeKey]: offset },
    labelOffset: offset,
    labelOffsetMode: modeKey,
  };
}

export const DEFAULT_SHOW_FIELDS = {
  name: true,
  dates: true,
  pct: true,
  units: true,
  baseline: false,
};

export const SHOW_FIELD_KEYS = [
  { key: 'name', label: 'Name' },
  { key: 'dates', label: 'Dates' },
  { key: 'pct', label: '%' },
  { key: 'units', label: 'Units' },
  { key: 'baseline', label: 'Baseline' },
];

export const MIN_BALLOON_SCALE = 0.7;
export const MAX_BALLOON_SCALE = 2;
export const DEFAULT_BALLOON_SCALE = 1;

export function clampBalloonScale(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_BALLOON_SCALE;
  return Math.min(MAX_BALLOON_SCALE, Math.max(MIN_BALLOON_SCALE, Math.round(n * 20) / 20));
}

export function markerShowFields(marker) {
  return { ...DEFAULT_SHOW_FIELDS, ...(marker?.style?.showFields || {}) };
}

/** Per-marker scale wins; otherwise the board default. */
export function markerBalloonScale(marker, boardScale) {
  const raw = marker?.style?.balloonScale;
  if (raw != null && Number.isFinite(Number(raw))) return clampBalloonScale(raw);
  return clampBalloonScale(boardScale);
}

export function fmtGaDate(d) {
  if (!d) return '—';
  const date = new Date(`${String(d).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-CA', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/\./g, '');
}

export function fmtGaUnits(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return null;
  const v = Number(n);
  if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return String(Math.round(v * 10) / 10);
}

/**
 * One source of truth for balloon wording. Screen HTML and the PDF painter
 * both consume this so a line never has to be inferred from CSS.
 */
export function buildBalloonChip({
  title,
  fields,
  resolved,
  delta,
  late = false,
  color,
  fill,
  borderColor,
  scale,
} = {}) {
  const f = { ...DEFAULT_SHOW_FIELDS, ...(fields || {}) };
  const lines = [];
  const bound = !!(resolved && resolved.matchedCount > 0);

  const titleBits = [];
  if (f.name && title) titleBits.push(String(title));
  if (f.pct && bound && resolved.pct !== null && resolved.pct !== undefined) {
    titleBits.push(`${resolved.pct}%`);
  }
  if (titleBits.length) {
    lines.push({
      kind: 'title',
      text: titleBits.join('  '),
      color: '#0f172a',
      fontWeight: 700,
      fontSize: 11,
    });
  }

  if (!bound) {
    lines.push({
      kind: 'unbound',
      text: 'not bound',
      color: '#94a3b8',
      fontWeight: 400,
      fontSize: 11,
      italic: true,
    });
  } else {
    if (f.dates) {
      const startTxt = resolved.start
        ? `${fmtGaDate(resolved.start)}${resolved.startIsActual ? ' A' : ''}`
        : '—';
      const finishTxt = resolved.finish
        ? `${fmtGaDate(resolved.finish)}${resolved.finishIsActual ? ' A' : ''}`
        : '—';
      lines.push({
        kind: 'dates',
        text: `${startTxt} → ${finishTxt}${late ? ' ⚠' : ''}`,
        color: '#334155',
        fontWeight: 400,
        fontSize: 11,
      });
    }
    if (f.baseline && (resolved.targetStart || resolved.targetFinish)) {
      lines.push({
        kind: 'baseline',
        text: `BL ${resolved.targetStart ? fmtGaDate(resolved.targetStart) : '—'} → ${resolved.targetFinish ? fmtGaDate(resolved.targetFinish) : '—'}`,
        color: '#64748b',
        fontWeight: 400,
        fontSize: 10,
      });
    }
    if (f.units && (resolved.budgetUnits != null || resolved.actualUnits != null || resolved.remainingUnits != null)) {
      lines.push({
        kind: 'units',
        text: `Bud ${fmtGaUnits(resolved.budgetUnits) ?? '—'} · Act ${fmtGaUnits(resolved.actualUnits) ?? '—'} · Rem ${fmtGaUnits(resolved.remainingUnits) ?? '—'}`,
        color: '#475569',
        fontWeight: 400,
        fontSize: 10.5,
      });
    }
  }

  const slipDays = delta && (delta.finishSlipDays || delta.startSlipDays);
  if (slipDays) {
    const n = delta.finishSlipDays ?? delta.startSlipDays;
    lines.push({
      kind: 'slip',
      text: `${n > 0 ? '▲' : '▼'} ${Math.abs(n)}d vs prev.`,
      color: n > 0 ? '#dc2626' : '#16a34a',
      fontWeight: 700,
      fontSize: 11,
    });
  }

  return {
    lines,
    color: color || '#64748b',
    fill: fill || 'rgba(255,255,255,0.97)',
    borderColor: borderColor || color || '#64748b',
    // Render scale includes overlayScale (stage px / 1200). Do not clamp to
    // the 70–200% user setting — that was already applied by the caller.
    scale: Number(scale) > 0 ? Number(scale) : 1,
  };
}

export function estimateTextWidth(text, fontSize, fontWeight = 400) {
  const unit = fontWeight >= 600 ? 0.62 : 0.56;
  return String(text || '').length * fontSize * unit;
}

const CHIP_PAD_X = 7;
const CHIP_PAD_Y = 3;
const CHIP_DOT = 8;
const CHIP_DOT_GAP = 5;
const CHIP_DESCENDER = 0.22;
const CHIP_LINE_GAP = 0.18;

/**
 * Stack balloon lines with explicit pixel boxes. html2canvas cannot be trusted
 * to honor flex / unitless line-height; this layout is what the PDF paints.
 */
export function layoutBalloonChip(chip, measure = estimateTextWidth) {
  const s = Number(chip?.scale) > 0 ? Number(chip.scale) : 1;
  const lines = [];
  let yCursor = CHIP_PAD_Y * s;
  let maxW = 0;

  for (const line of chip?.lines || []) {
    const fontSize = (line.fontSize || 11) * s;
    const fontWeight = line.fontWeight || 400;
    const textW = measure(line.text, fontSize, fontWeight);
    const extra = line.kind === 'title' ? (CHIP_DOT * s + CHIP_DOT_GAP * s) : 0;
    maxW = Math.max(maxW, textW + extra);
    const baseline = yCursor + fontSize;
    const top = yCursor;
    const bottom = baseline + fontSize * CHIP_DESCENDER;
    lines.push({
      kind: line.kind,
      text: line.text,
      color: line.color,
      italic: !!line.italic,
      fontWeight,
      fontSize,
      x: CHIP_PAD_X * s + extra,
      y: baseline,
      top,
      bottom,
      width: textW,
    });
    yCursor = bottom + fontSize * CHIP_LINE_GAP;
  }

  return {
    width: Math.max(72 * s, maxW + CHIP_PAD_X * 2 * s),
    height: yCursor + CHIP_PAD_Y * s,
    lines,
    scale: s,
    padX: CHIP_PAD_X * s,
    padY: CHIP_PAD_Y * s,
    dot: CHIP_DOT * s,
    dotGap: CHIP_DOT_GAP * s,
  };
}

/** True when any two line glyph boxes share vertical space (the PDF overlap bug). */
export function balloonLinesOverlap(layout) {
  const rows = layout?.lines || [];
  for (let i = 1; i < rows.length; i += 1) {
    if (rows[i].top < rows[i - 1].bottom - 0.01) return true;
  }
  return false;
}

/**
 * Saved balloon offset for the mode the canvas is actually drawing.
 * Sequence boards used to store positions under `margins`; when rendering as
 * `auto` with no auto slot, fall back to those so a drag-save is not lost.
 */
export function resolveSavedLabelOffset(marker, { labelMode, labelDraft, fallbackToMargins = false } = {}) {
  if (labelDraft && (labelDraft.markerId === marker.id || String(labelDraft.markerId) === String(marker.id))) {
    return labelDraft.offset || null;
  }
  const modeKey = offsetModeKey(labelMode);
  const perMode = marker.style?.labelOffsets?.[modeKey];
  if (perMode && typeof perMode.dx === 'number') return perMode;
  if (fallbackToMargins && modeKey === 'auto') {
    const margins = marker.style?.labelOffsets?.margins;
    if (margins && typeof margins.dx === 'number') return margins;
  }
  const legacy = marker.style?.labelOffset;
  if (legacy && typeof legacy.dx === 'number') {
    const legacyMode = marker.style?.labelOffsetMode;
    if (!legacyMode || legacyMode === modeKey) return legacy;
    if (fallbackToMargins && modeKey === 'auto' && legacyMode === 'margins') return legacy;
  }
  return null;
}
